import React from 'react'

import Dialog from '@material-ui/core/Dialog'

import { ShareTab, OptionsType } from './ShareTab'

import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import makeStyles from '@material-ui/core/styles/makeStyles'

import CloseIcon from '@material-ui/icons/Close'
import PeopleAltIcon from '@material-ui/icons/LockOutlined'
import PublicIcon from '@material-ui/icons/PublicOutlined'

import { OptionType, PlaybookType } from './types'
import { capitalize, option } from './utils'

const useStyles = makeStyles(theme => ({
    bold: {
        fontWeight: 600
    },
    content: {
        background: '#F5F5F5',
        overflow: 'hidden',
        borderRadius: 4
    },
    selectRoot: {
        '& ul': {
            width: '225px',
            padding: theme.spacing(1),
            '& li': {
                width: '100%',
                fontWeight: 500,
                fontSize: '14px',
                padding: theme.spacing(1.7, 2)
            }
        }
    },

    roundIcon: {
        background: '#EDEEEF',
        borderRadius: '50%',
        minWidth: '40px',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px'
    },
    select: {
        fontSize: '12px',
        fontWeight: 600,
        '&:before, &:after': {
            display: 'none'
        }
    },
    description: {
        color: '#828282',
        fontSize: '14px'
    }
}))

export type ShareOnlyDialogProps = {
    /**
     * a node to be rendered in the special component.
     */
    isOpen: boolean
    uid: string
    hideCopyLink?: boolean
    origin?: string
    onClose: () => void
    onShare: ({
        emails,
        userIds
    }: {
        emails: Array<string>
        userIds: Array<string>
    }) => void
    title: string
    steps: PlaybookType['steps']
    playbook: PlaybookType
    isLoading: boolean
    options?: OptionsType
    currentTime: number
    copyLinkPath: string
    onCopySharedLink: () => void
    onCopyGifLink: () => void
}

const VisibiltyLevel = {
    restricted: 'restricted',
    anyoneInOrg: 'anyoneInOrg',
    anyone: 'anyone'
}

const getPublicityList = (orgName: string) => [
    option(VisibiltyLevel.anyone, 'Public'),
    option(VisibiltyLevel.anyoneInOrg, capitalize(orgName)),
    option(VisibiltyLevel.restricted, 'Restricted')
]

const TeamIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <mask maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
            <rect width="24" height="24" fill="currentColor" />
        </mask>
        <g mask="url(#mask0_4072_10244)">
            <path
                d="M16.95 14.3C16.3167 14.3 15.7834 14.0833 15.35 13.65C14.9167 13.2166 14.7 12.6833 14.7 12.05C14.7 11.4333 14.9167 10.904 15.35 10.462C15.7834 10.0206 16.3167 9.79995 16.95 9.79995C17.5667 9.79995 18.096 10.0206 18.538 10.462C18.9794 10.904 19.2001 11.4333 19.2001 12.05C19.2001 12.6833 18.9794 13.2166 18.538 13.65C18.096 14.0833 17.5667 14.3 16.95 14.3ZM12.2 19.3V18.15C12.2 17.8 12.2877 17.475 12.463 17.175C12.6377 16.875 12.9 16.6666 13.25 16.55C13.8167 16.3 14.4127 16.1126 15.038 15.988C15.6627 15.8626 16.3 15.8 16.95 15.8C17.5834 15.8 18.2084 15.8626 18.8251 15.988C19.4417 16.1126 20.05 16.3 20.65 16.55C20.9834 16.6666 21.2417 16.875 21.425 17.175C21.6084 17.475 21.7001 17.8 21.7001 18.15V19.3H12.2ZM9.80005 11.7C8.85005 11.7 8.02938 11.354 7.33805 10.662C6.64605 9.97062 6.30005 9.14995 6.30005 8.19995C6.30005 7.23328 6.64605 6.40828 7.33805 5.72495C8.02938 5.04162 8.85005 4.69995 9.80005 4.69995C10.7667 4.69995 11.5917 5.04162 12.275 5.72495C12.9584 6.40828 13.3 7.23328 13.3 8.19995C13.3 9.14995 12.9584 9.97062 12.275 10.662C11.5917 11.354 10.7667 11.7 9.80005 11.7ZM2.30005 19.3V17.075C2.30005 16.575 2.43338 16.1166 2.70005 15.7C2.96672 15.2833 3.32505 14.9666 3.77505 14.75C4.70838 14.2833 5.68338 13.925 6.70005 13.675C7.71671 13.425 8.75005 13.3 9.80005 13.3C10.3 13.3 10.796 13.3373 11.288 13.412C11.7794 13.4873 12.275 13.575 12.775 13.675L11.5 14.975C11.2167 14.9083 10.9334 14.8623 10.65 14.837C10.3667 14.8123 10.0834 14.8 9.80005 14.8C8.86672 14.8 7.95005 14.9083 7.05005 15.125C6.15005 15.3416 5.28338 15.6666 4.45005 16.1C4.26672 16.2 4.11238 16.3333 3.98705 16.5C3.86238 16.6666 3.80005 16.8583 3.80005 17.075V17.8H9.80005V19.3H2.30005ZM9.80005 10.2C10.35 10.2 10.821 10.004 11.213 9.61195C11.6044 9.22062 11.8 8.74995 11.8 8.19995C11.8 7.64995 11.6044 7.17895 11.213 6.78695C10.821 6.39562 10.35 6.19995 9.80005 6.19995C9.25005 6.19995 8.77905 6.39562 8.38705 6.78695C7.99572 7.17895 7.80005 7.64995 7.80005 8.19995C7.80005 8.74995 7.99572 9.22062 8.38705 9.61195C8.77905 10.004 9.25005 10.2 9.80005 10.2Z"
                fill="currentColor"
            />
        </g>
    </svg>
)

const staticData = {
    restricted: {
        visibilityIcon: <PeopleAltIcon />,
        visibilityMessage: 'Only members and spaces added can open this link'
    },
    anyoneInOrg: {
        visibilityIcon: <TeamIcon />,
        visibilityMessage: 'All members in this account can view'
    },
    anyone: {
        visibilityIcon: <PublicIcon />,
        visibilityMessage: 'Anyone with the link can view'
    }
} as const

const getActiveOption = (
    list: Array<OptionType>,
    isPublic?: boolean,
    isMainIncluded?: boolean | null
) => {
    if (isPublic)
        return list.find(({ value }) => value === VisibiltyLevel.anyone)
    if (isMainIncluded)
        return list.find(({ value }) => value === VisibiltyLevel.anyoneInOrg)
    return list.find(({ value }) => value === VisibiltyLevel.restricted)
}

export function ShareOnlyDialog({
    isLoading,
    isOpen,
    onClose,
    title,
    playbook,
    ...props
}: ShareOnlyDialogProps) {
    const classes = useStyles()

    if (!playbook) return null

    const { isPublic, isMainIncluded, orgName = 'Organization' } = playbook
    const publicity = getPublicityList(orgName)
    const linkType =
        getActiveOption(publicity, isPublic, isMainIncluded) || publicity[0]

    const { visibilityIcon, visibilityMessage } = staticData[linkType.value]

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <Box position="absolute" top={0} width="100%">
                {isLoading && <LinearProgress />}
            </Box>

            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                pt={2}
                px={2}
            >
                <Typography className={classes.bold} data-cy="dialog-title">
                    {title}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    data-cy="close-button"
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box display="flex" alignItems="center" p={2}>
                <Box mr={1} className={classes.roundIcon}>
                    {visibilityIcon}
                </Box>
                <Box>
                    <Box
                        color="rgba(0, 0, 0, 0.54) !important"
                        fontSize="14px"
                        style={{ fontWeight: 600 }}
                        fontFamily="Roboto, sans-serif"
                    >
                        {linkType.label}
                    </Box>
                    <Typography className={classes.description}>
                        {visibilityMessage}
                    </Typography>
                </Box>
            </Box>

            <Box className={classes.content} m={2}>
                <ShareTab
                    playbook={playbook}
                    onClose={onClose}
                    isLoading={isLoading}
                    {...props}
                />
            </Box>
        </Dialog>
    )
}
